import { z } from "zod";

import { HelpChatFeedback, HelpChatRole } from "@signalist/shared-types";

import { HelpArticleSourceSchema } from "./helpCenter.schema";

/** Route pattern / label the user was on when a turn was asked. */
export const HelpMessagePageContextSchema = z.object({
	routePattern: z.string().optional(),
	label: z.string().optional(),
	activeTab: z.string().optional(),
});

/** One transcript turn in the support-review detail view. */
export const HelpConversationMessageInfoSchema = z.object({
	id: z.number(),
	role: z.enum(HelpChatRole),
	content: z.string(),
	sources: HelpArticleSourceSchema.array().nullable(),
	pageContext: HelpMessagePageContextSchema.nullable(),
	feedback: z.enum(HelpChatFeedback).nullable(),
	createdAt: z.string(),
});

/** A conversation row in the review list. */
export const HelpConversationSummarySchema = z.object({
	id: z.number(),
	userId: z.number().nullable(),
	companyId: z.number().nullable(),
	language: z.string().nullable(),
	messageCount: z.coerce.number(),
	firstQuestion: z.string().nullable(),
	upCount: z.coerce.number(),
	downCount: z.coerce.number(),
	createdAt: z.string(),
	lastMessageAt: z.string(),
});

/** A full transcript (summary + ordered turns). */
export const HelpConversationDetailSchema = HelpConversationSummarySchema.extend({
	messages: HelpConversationMessageInfoSchema.array(),
});

/** Pagination envelope returned by the list endpoint (PageDTO). */
export const HelpConversationPageMetaSchema = z.object({
	page: z.coerce.number(),
	limit: z.coerce.number(),
	itemCount: z.coerce.number(),
	pageCount: z.coerce.number(),
	hasPreviousPage: z.boolean(),
	hasNextPage: z.boolean(),
});

export const HelpConversationPageSchema = z.object({
	data: HelpConversationSummarySchema.array(),
	meta: HelpConversationPageMetaSchema,
});

export type HelpConversationMessageInfo = z.infer<typeof HelpConversationMessageInfoSchema>;
export type HelpConversationSummary = z.infer<typeof HelpConversationSummarySchema>;
export type HelpConversationDetail = z.infer<typeof HelpConversationDetailSchema>;
export type HelpConversationPageMeta = z.infer<typeof HelpConversationPageMetaSchema>;
export type HelpConversationPage = z.infer<typeof HelpConversationPageSchema>;

/** Query args for the review list. */
export interface HelpConversationListArgs {
	page: number;
	limit: number;
	searchValue?: string;
	language?: string;
	feedback?: HelpChatFeedback;
	createdFrom?: string;
	createdTo?: string;
}

/** Args for setting thumbs up/down on an assistant answer (feedback null clears it). */
export interface SetHelpFeedbackArgs {
	messageId: number;
	feedback: HelpChatFeedback | null;
}
